import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { message } from 'antd';
import REDUCER_KEY from 'reducers/reducer.keys';
import { Single } from 'components/Page';
import { convertLocalToUtcDate, formatDateTime } from 'library/time';
import { canViewOptimiser } from 'library/optimiser';
import {
  loadSummary as loadSummaryAction,
  loadViolationsForRecord as loadViolationsForRecordAction
} from 'actions/optimiserStatus';
import OptimiserReadinessWarning from 'components/OptimiserReadinessWarning';
import OptimiserExclusionWarning from 'components/OptimiserExclusionWarning';
import config from './config';

/*
 ** @description Prepare data for API
 ** @params Object start_date, end_date, ...rest
 ** @return Object
 */
const handlePrepareData = ({ start_date, end_date, ...rest }) => ({
  ...rest,
  start_date: formatDateTime(convertLocalToUtcDate(start_date), 'YYYY-MM-DD'),
  end_date: formatDateTime(convertLocalToUtcDate(end_date), 'YYYY-MM-DD')
});

const UserProfile = ({
  objectId, permissions, loadSummary, loadViolationsForRecord
}) => {
  const [readinessViolations, setReadinessViolations] = useState(undefined);
  const [exclusionWarning, setExclusionWarning] = useState(false);

  const onSuccessCallback = (response, data, item, postSuccessCallback) => {
    postSuccessCallback();
    setExclusionWarning(!!(response && response.optimiser_exclusion_warning));
    if (!canViewOptimiser()) return;
    // objectId is the user this profile belongs to (the parent record id the tabbed
    // layout threads in) — the record the readiness re-check targets, and always
    // present when editing. The saved-record fields are defensive fallbacks: the save
    // response is the unwrapped .toJS() object (see the add/update thunks), so it has
    // no `.data` wrapper and the profile carries `user_id`, not `id`.
    const savedRecord = response || {};
    const userId = objectId
      || savedRecord.user_id
      || savedRecord.id
      || (item && item.id);
    if (!userId) return;
    loadSummary();
    loadViolationsForRecord({ recordId: userId, recordType: 'user' })
      .then((violations) => {
        setReadinessViolations(violations);
      })
      .catch(() => {
        message.warning('Optimiser status check could not be completed');
      });
  };

  return (
    <div>
      <Single
        canAdmin={permissions.CanAdminUsers().hasPermissions()}
        reducer={{
          item: REDUCER_KEY.userProfile,
          list: REDUCER_KEY.peopleData
        }}
        config={{
          ...config,
          parent: {
            id: objectId
          }
        }}
        onPrepareData={handlePrepareData}
        onSuccessCallback={onSuccessCallback}
        permissions={permissions}
      />
      {readinessViolations && readinessViolations.length > 0 && (
        <OptimiserReadinessWarning
          violations={readinessViolations}
          onClose={() => setReadinessViolations(undefined)}
        />
      )}
      <OptimiserExclusionWarning
        visible={exclusionWarning}
        onClose={() => setExclusionWarning(false)}
      />
    </div>
  );
};

UserProfile.propTypes = {
  objectId: PropTypes.number,
  permissions: PropTypes.object.isRequired,
  loadSummary: PropTypes.func,
  loadViolationsForRecord: PropTypes.func
};

UserProfile.defaultProps = {
  objectId: null,
  loadSummary() {},
  loadViolationsForRecord() {
    return Promise.resolve([]);
  }
};

const mapDispatchToProps = dispatch => ({
  loadSummary() {
    return dispatch(loadSummaryAction());
  },
  loadViolationsForRecord(props) {
    return dispatch(loadViolationsForRecordAction(props));
  }
});

export default connect(null, mapDispatchToProps)(UserProfile);
